import { motion } from "framer-motion";
import {
  Sparkles, Camera, Palette, Wand2, Users, Heart, ArrowRight,
  Calendar, Trash2, TrendingUp, Droplet,
} from "lucide-react";

const UPCOMING = [
  {
    icon: Users,
    title: "Stylist Marketplace",
    eyebrow: "Phase 4 · Marketplace",
    desc: "Browse real-world stylists, view their seasonal collections, and shop pieces directly. Stylists get a dedicated dashboard to upload looks, set prices, sizes, and lead times. We take care of payments and delivery.",
    color: "accent",
  },
  {
    icon: Camera,
    title: "OutfitJudge AI",
    eyebrow: "Phase 5 · AI",
    desc: "Snap any garment in a shop or your closet — our AI gives an honest verdict: BUY, MAYBE, or LEAVE, with color-coded reasoning around fit, vibe, and your existing wardrobe.",
    color: "default",
  },
  {
    icon: Heart,
    title: "Shop Recommendations 2.0",
    eyebrow: "Phase 5 · Shop",
    desc: "Best-pick items for your wardrobe gaps with direct buy + 'shop similar' links across Myntra, Amazon, Ajio, H&M and more.",
    color: "default",
  },
  {
    icon: Droplet,
    title: "Color Palette Analyzer",
    eyebrow: "Phase 5 · Style DNA",
    desc: "Deep body & seasonal color analysis — discover the exact palette that flatters your undertone and the shades to avoid, with shoppable swatches.",
    color: "default",
  },
  {
    icon: Calendar,
    title: "Outfit of the Week",
    eyebrow: "Phase 5 · Auto",
    desc: "Auto-curated weekly look based on your calendar events, weather, and last week's wears — delivered every Monday.",
    color: "default",
  },
  {
    icon: Trash2,
    title: "Closet Cleanout",
    eyebrow: "Phase 5 · Wardrobe",
    desc: "AI scans your closet, surfaces pieces you haven't worn in 90+ days, and suggests donate / resell / archive with one tap.",
    color: "default",
  },
  {
    icon: TrendingUp,
    title: "Trend Forecaster",
    eyebrow: "Phase 5 · Editorial",
    desc: "A personalized weekly trend brief — which silhouettes, colors, and labels are rising for your style personality.",
    color: "default",
  },
];

export default function ComingSoon() {
  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12 lg:py-20">
      <p className="eyebrow flex items-center gap-2"><Sparkles size={12} /> Stylebay · Roadmap</p>
      <h1 className="editorial-h1 mt-3">What&apos;s coming.</h1>
      <p className="text-sm text-muted-foreground mt-3 max-w-xl">
        A glimpse at what the studio is sketching next. Tap any tile to be the first to know when it ships.
      </p>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
        {UPCOMING.map((u, i) => {
          const Icon = u.icon;
          const featured = u.color === "accent";
          return (
            <motion.div
              key={u.title}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              data-testid={`coming-${u.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`relative border p-7 lg:p-10 transition-all hover:-translate-y-1 ${
                featured
                  ? "bg-accent text-accent-foreground border-accent"
                  : "border-border bg-card hover:border-foreground"
              }`}
            >
              <div className={`h-12 w-12 inline-flex items-center justify-center mb-5 ${
                featured ? "bg-accent-foreground/15" : "bg-muted"
              }`}>
                <Icon size={22} strokeWidth={1.4} />
              </div>
              <p className={`text-[10px] uppercase tracking-[0.2em] ${featured ? "opacity-80" : "text-muted-foreground"}`}>
                {u.eyebrow}
              </p>
              <h2 className="font-serif text-3xl mt-2 leading-tight">{u.title}</h2>
              <p className={`text-sm mt-3 leading-relaxed ${featured ? "opacity-90" : "text-muted-foreground"}`}>
                {u.desc}
              </p>
              <p className={`mt-6 text-[11px] uppercase tracking-[0.22em] inline-flex items-center gap-2 ${
                featured ? "opacity-90" : ""
              }`}>
                In studio <ArrowRight size={12} />
              </p>
            </motion.div>
          );
        })}
      </div>

      <div className="mt-16 border-t border-border pt-10 text-center">
        <p className="eyebrow">Have a request?</p>
        <p className="font-serif text-2xl mt-2">Tell us what you want next.</p>
        <p className="text-sm text-muted-foreground mt-2">
          Hit the Stylist chat — every suggestion shapes the next sprint.
        </p>
      </div>
    </div>
  );
}
